import {
  AudiologistCall64,
  OrkaTwoSingle64,
  ShoppingBagTick64
} from '@evocolabs/icons'
import { Box, useMediaQuery } from '@mui/material'
import { Button, Typography } from '@evocolabs/orkaui'
import Navigation, { NavigationProps } from '@/src/utils/Navigation'
import {
  _gtmEventAddToCart,
  _gtmEventCheckout,
  _gtmEventProductDetailsImpression,
  _gtmEventProductImpression,
  gtmDataLayerPushEvents
} from '@/utils/gtmDataLayerUtils'
import { useEffect, useState } from 'react'

import Footer from '@/src/components/Footer/Footer2'
import Header from '@/src/components/HeaderV2'
import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { selector as baseSelector } from '@/src/redux/store'
import dynamic from 'next/dynamic'
import { getEnvVar } from '@/env'
import joinCDN from '@/src/utils/CDN'
import style from '../scss/OrkaTwo.module.scss'
import theme from '@/src/theme'
import { useSelector } from 'react-redux'

const ProductEvaluate = dynamic(
  () => import('@/src/components/Product/OrkaTwo/ProductEvaluate')
)

const ProductFAQ = dynamic(
  () => import('@/src/components/Product/OrkaTwo/ProductFAQ')
)

const ProductImg = dynamic(
  () => import('@/src/components/Product/OrkaTwo/ProductImg')
)

const ProductInfo = dynamic(
  () => import('@/src/components/Product/OrkaTwo/ProductInfo')
)

const ProductParameter = dynamic(
  () => import('@/src/components/Product/OrkaTwo/ProductParameter')
)

const MobilePayContinueData = {
  full: {
    text: 'From ',
    price: '$1899'
  },
  month: {
    text1: 'or as low as ',
    price: '$80/mo*',
    text2: ''
  },
  btnText: 'Continue'
}

const getPath = (email = '') => {
  const payload = [
    {
      quantity: 1,
      product_id: getEnvVar('NEXT_PUBLIC_GOODS_ID'),
      email
    }
  ]
  const path = `/Checkout?bigcommerce=${encodeURIComponent(
    JSON.stringify(payload)
  )}`
  return path
}

const navigationProps: NavigationProps = {
  title: 'Need More Information?',
  titleColor: theme.palette.grey[800],
  pageId: 'buy-orka-two-page',
  sections: [
    {
      icon: <ShoppingBagTick64 />,
      subtitle: 'All set?',
      text: 'Check out now and get Orka Two delivered to your door.',
      CTAText: 'Out of Stock',
      CTAHref: getPath(),
      CTAType: 'Button'
    },
    {
      icon: <OrkaTwoSingle64 />,
      subtitle: 'Orka Two Hearing Aids',
      text: 'Learn more about how the unique features of Orka Two can help improve your hearing.',
      CTAText: 'Explore Orka Two',
      CTAHref: '/OrkaTwo',
      CTAType: 'Button'
    },
    {
      icon: <AudiologistCall64 />,
      subtitle: 'Free Care Service',
      text: 'Get unlimited professional consultations and device adjustments throughout your journey with us.',
      CTAText: 'Try OrkaCare',
      CTAHref: '/OrkaCare',
      CTAType: 'Button'
    }
  ]
}

const MobilePayContinue = function () {
  const [checkoutButtonEnable, setCheckoutButtonEnable] = useState(false)
  const userInfo = useSelector(baseSelector.selectUserInfo)
  const email = userInfo?.email
  const [path, setPath] = useState(getPath())

  useEffect(() => {
    if (email) {
      setPath(getPath(email))
      setCheckoutButtonEnable(true)
    }
  }, [email])

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) {
      setCheckoutButtonEnable(true)
    }
    gtmDataLayerPushEvents(_gtmEventProductImpression)
    gtmDataLayerPushEvents(_gtmEventProductDetailsImpression)
  }, [])

  const clickHandler = async () => {
    gtmDataLayerPushEvents(_gtmEventCheckout)
    gtmDataLayerPushEvents(_gtmEventAddToCart)
    return new Promise((res) => setTimeout(res, 1000 * 30))
  }

  return (
    <>
      <Box display="flex" flexDirection="column" flex="1">
        <Typography variant="body4" mr="2px">
          {MobilePayContinueData.full.text}
          <Typography variant="Body3" color="#26201F">
            {MobilePayContinueData.full.price}
          </Typography>
        </Typography>
        <Typography variant="body4">
          {MobilePayContinueData.month.text1}
          <Typography variant="Body3" color="#26201F">
            {MobilePayContinueData.month.price}
          </Typography>
          {MobilePayContinueData.month.text2}
        </Typography>
      </Box>
      <Box display="flex" alignItems="baseline">
        <Link href={path}>
          <Button
            id="productbuy_continue"
            variant="primary"
            size="medium"
            block
            disabled={!checkoutButtonEnable}
            sx={{
              width: '97px',
              height: '56px',
              marginLeft: '12px',
              marginRight: '54px'
            }}
            onClick={clickHandler}
          >
            {MobilePayContinueData.btnText}
          </Button>
        </Link>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          width: '100vw',
          height: '1px',
          transform: 'scaleY(.1)',
          bottom: 0,
          left: '-16px',
          boxShadow: '0px -2px 8px rgba(0, 0, 0, 0.35)'
        }}
      />
    </>
  )
}

const OrkaTwo = function () {
  const usePC = useMediaQuery(theme.breakpoints.up('md'))
  return (
    <>
      <NextSeo
        title="Orka Two Hearing Aids | No Tiered Pricing | Buy Online"
        description="Orka Two hearing aids, an ultimate hearing solution at an affordable price."
      />
      <Header isWhiteBg />
      <Box className={style['orka-two-box']}>
        <Box className={style['orka-two-index']}>
          <Box
            display="flex"
            sx={{
              position: 'relative',
              flexWrap: {
                xs: 'wrap',
                md: 'nowrap'
              },
              flexDirection: {
                xs: 'column',
                md: 'column',
                dl: 'row',
                lg: 'row'
              }
            }}
          >
            <ProductImg />
            <Box className={style['product-paramter-box']}>
              <ProductParameter />
            </Box>
          </Box>
          <Box mt={{ xs: '40px', dl: '80px' }}>
            <ProductInfo />
          </Box>
          <ProductEvaluate />
        </Box>
      </Box>
      <Navigation {...navigationProps} />
      <ProductFAQ />
      <Footer />
    </>
  )
}

export default OrkaTwo
